import type { AttachmentDto } from '@keep/shared';
import { Trash2 } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Images en tête de note, disposées en rangées comme dans Keep :
 * 1 image pleine largeur, puis des rangées de 2 ou 3.
 */
function rows<T>(items: T[]): T[][] {
  if (items.length <= 1) return items.length ? [items] : [];
  const result: T[][] = [];
  let rest = items;
  while (rest.length) {
    const size = rest.length === 4 ? 2 : Math.min(3, rest.length);
    result.push(rest.slice(0, size));
    rest = rest.slice(size);
  }
  return result;
}

export function AttachmentGrid({
  attachments,
  onRemove,
  maxRows,
  className,
}: {
  attachments: AttachmentDto[];
  onRemove?: (attachment: AttachmentDto) => void;
  /** Sur une carte, on limite le nombre de rangées pour garder des cartes compactes. */
  maxRows?: number;
  className?: string;
}) {
  const grid = rows(attachments).slice(0, maxRows);
  if (grid.length === 0) return null;
  return (
    <div className={cn('flex flex-col gap-px overflow-hidden', className)}>
      {grid.map((row, index) => (
        <div key={index} className="flex gap-px">
          {row.map((attachment) => (
            <div key={attachment.id} className="group/img relative min-w-0 flex-1">
              <img
                src={attachment.url}
                alt=""
                loading="lazy"
                draggable={false}
                className={cn(
                  'block w-full object-cover',
                  row.length > 1 ? 'h-32' : 'max-h-[420px]',
                )}
              />
              {onRemove && (
                <button
                  type="button"
                  aria-label="Supprimer l'image"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove(attachment);
                  }}
                  className="absolute right-2 bottom-2 hidden size-8 items-center justify-center rounded bg-black/60 text-white group-hover/img:flex hover:bg-black/80"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
